/**
 * Consolidated hook for all cart-related operations
 * Combines: useCartInitialization + useCartSuccess + useCartHandlers
 */

import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import nvCookieHelpers from '../../../services/cookieService';
import { useHeaderMenu } from '../../../contexts/login.context';
import { CartData } from '../../../interfaces/CartData';
import { Address } from '../../../interfaces/Address';
import * as intrfce from '../Interfaces';
import { createAddressFromCartData, savedAddressToFormAddress, applyUSAddressFieldLimits } from '../utils';

interface UseCartOperationsProps {
  // Cart data
  orderState: string | null;
  cartData: CartData | undefined;
  currencySign: string;
  locale: string;
  countyField: boolean;
  stateDropdown: boolean;
  userType: string | undefined;
  
  // Address state
  savedBillingAddress: intrfce.iSavedAddress[];
  savedShippingAddress: intrfce.iSavedAddress[];
  formBillingAddress: intrfce.iAddressFormData;
  formFieldsRef: React.MutableRefObject<any>;
  
  // Setters
  setBrand: (brand: string) => void;
  setFormTaxData: (data: intrfce.iTaxFormData) => void;
  setIsBusinessPurchase: (value: boolean) => void;
  setIsBilling: (value: boolean) => void;
  setCartData: (data: any) => void;
  setShippingList: (list: any[]) => void;
  setShippingOptions: (options: intrfce.iShippingDetails) => void;
  setSavedBillingAddress: (addresses: intrfce.iSavedAddress[] | ((prev: intrfce.iSavedAddress[]) => intrfce.iSavedAddress[])) => void;
  setSavedShippingAddress: (addresses: intrfce.iSavedAddress[] | ((prev: intrfce.iSavedAddress[]) => intrfce.iSavedAddress[])) => void;
  setFormBillingAddress: (address: intrfce.iAddressFormData | ((prev: intrfce.iAddressFormData) => intrfce.iAddressFormData)) => void;
  setFormShippingAddress: (address: intrfce.iAddressFormData | ((prev: intrfce.iAddressFormData) => intrfce.iAddressFormData)) => void;
  setSelectedBillingAddress: (address: intrfce.iSavedAddress | null | "reset" | ((prev: intrfce.iSavedAddress | null | "reset") => intrfce.iSavedAddress | null | "reset")) => void;
  setIsBillingReadonly: (value: boolean) => void;
  setSelectedShippingAddress: (address: intrfce.iSavedAddress | null | "reset" | ((prev: intrfce.iSavedAddress | null | "reset") => intrfce.iSavedAddress | null | "reset")) => void;
  setIsShippingReadonly: (value: boolean) => void;
  setPromoErr: React.Dispatch<React.SetStateAction<boolean>>;
  setValidationErrors: React.Dispatch<React.SetStateAction<any>>;
  
  // Functions
  fetchCart: () => Promise<void>;
  emptyCart: (message: string, pid?: number) => void;
  hideBodyLoader: () => void;
  showBodyLoader: () => void;
  handleSavedBillingAddressChange?: (fieldName: string, selectedValue: any, addrArr?: any) => void;
  updateCart: (handleSuccess: (result: any, productId?: number) => void, handleError: (err: string, productId?: number) => void, productId?: string, selectedOption?: number, shippingOption?: string, coupon?: string, currency?: any, couponApplied?: boolean) => Promise<void>;
  reviewOrder: (user: string) => void;
  getStateValue: (value: any, fallback: any) => any;
  orderAccount: () => void;
  t: (key: string) => string;
}

export const useCartOperations = (props: UseCartOperationsProps) => {
  const {
    orderState,
    cartData,
    currencySign,
    locale,
    countyField,
    stateDropdown,
    userType,
    savedBillingAddress,
    savedShippingAddress,
    setBrand,
    setFormTaxData,
    setIsBusinessPurchase,
    setIsBilling,
    setCartData,
    setShippingList,
    setShippingOptions,
    setSavedBillingAddress,
    setSavedShippingAddress,
    setFormBillingAddress,
    setFormShippingAddress,
    setSelectedBillingAddress,
    setIsBillingReadonly,
    setSelectedShippingAddress,
    setIsShippingReadonly,
    setPromoErr,
    setValidationErrors,
    fetchCart,
    emptyCart,
    hideBodyLoader,
    showBodyLoader,
    handleSavedBillingAddressChange,
    updateCart,
    reviewOrder,
    orderAccount,
    t
  } = props;

  const navigate = useNavigate();
  const { setItemsInCart } = useHeaderMenu();

  /**
   * Handles cart initialization on component mount
   */
  useEffect(() => {
    const sessionexpiredID = nvCookieHelpers.getCookie("session_expired");
    const isSessionValid = sessionexpiredID && !JSON.parse(sessionexpiredID);

    // Check if we're just navigating between steps (not initial load)
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get("step");
    const isNavigatingBetweenSteps = stepParam === "1" || stepParam === "2";

    if (orderState) {
      if (isSessionValid) {
        fetchCart();
      } else {
        emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
      }
      hideBodyLoader();
      return;
    }

    if (!cartData) {
      if (isSessionValid) {
        fetchCart();
      } else {
        emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
      }
      return;
    }

    // Skip address updates if we're just navigating between steps
    // handleSuccess will handle the updates when fetchCart completes
    if (isNavigatingBetweenSteps) {
      return;
    }

    // Initialize cart data
    const parsedFormData = cartData;
    showBodyLoader();

    // Handle billing address
    if (parsedFormData?.billingAddress) {
      const billing = parsedFormData.billingAddress;
      
      if (Number(billing.id)) {
        handleSavedBillingAddressChange?.("savedBillingAddress", "reset");

        setFormBillingAddress((prev) => {
          // Helper function to check if a value is a non-empty string
          const hasValue = (val: any) => val && typeof val === 'string' && val.trim().length > 0;
          
          // For logged-in users, email comes from cartData.emailAddress (root level)
          const isLoggedIn = nvCookieHelpers.getCookie("isUserLoggedIn");
          const billingEmail = hasValue(billing.emailAddress) ? billing.emailAddress : null;
          const cartEmail = hasValue(parsedFormData?.emailAddress) ? parsedFormData.emailAddress : null;
          const emailToUse = isLoggedIn 
            ? (cartEmail || billingEmail || prev?.email || "")
            : (billingEmail || prev?.email || "");
          
          return {
            ...prev,
            ...(billing.id ? { id: Number(billing.id) } : {}),
            // Only update fields if cart has non-empty values, otherwise preserve existing
            pincode: hasValue(billing.postalCode) ? billing.postalCode : (prev?.pincode || ""),
            address: hasValue(billing.line1) ? billing.line1 : (prev?.address || ""),
            line2: hasValue(billing.line2) ? billing.line2 : (prev?.line2 || ""),
            cityValue: hasValue(billing.city) ? billing.city : (prev?.cityValue || ""),
            stateValue:
              (stateDropdown || locale === 'zh-tw')
                ? (locale === 'zh-tw' ? "TW" : (hasValue(billing.countrySubdivision) ? billing.countrySubdivision : (prev?.stateValue || '')))
                : (prev?.stateValue || ''),
            county: countyField ? (hasValue(billing.county) ? billing.county : (prev?.county || "")) : (prev?.county || ''),
            countryName: hasValue(billing.countryName) ? billing.countryName : (prev?.countryName || ""),
            countryValue: hasValue(billing.country) ? billing.country : (prev?.countryValue || ""),
            firstName: hasValue(billing.firstName) ? billing.firstName : (prev?.firstName || ""),
            lastName: hasValue(billing.lastName) ? billing.lastName : (prev?.lastName || ""),
            contact: hasValue(billing.phoneNumber) ? billing.phoneNumber : (prev?.contact || ""),
            // Email fields - for logged-in users, prioritize cartData.emailAddress
            email: emailToUse,
            vemail: emailToUse
          };
        });

        setSelectedBillingAddress({
          city: billing.city || "",
          countryName: billing.countryName,
          country: billing.country,
          firstName: billing.firstName || "",
          lastName: billing.lastName || "",
          line1: billing.line1 || "",
          line2: billing.line2,
          nickName: "",
          phoneNumber: billing.phoneNumber || "",
          postalCode: billing.postalCode || "",
          countrySubdivision: billing.countrySubdivision || "",
          ...(countyField && { county: billing.county }),
        });
      }
    }

    // Handle shipping address
    if (parsedFormData.shippingAddress && parsedFormData.billingAddress) {
      const shipping = parsedFormData.shippingAddress;

      setFormShippingAddress((prev) => {
        // Helper function to check if a value is a non-empty string
        const hasValue = (val: any) => val && typeof val === 'string' && val.trim().length > 0;
        
        // For logged-in users, email comes from cartData.emailAddress (root level)
        const isLoggedIn = nvCookieHelpers.getCookie("isUserLoggedIn");
        const shippingEmail = hasValue(shipping?.emailAddress) ? shipping.emailAddress : null;
        const cartEmail = hasValue(parsedFormData?.emailAddress) ? parsedFormData.emailAddress : null;
        const emailToUse = isLoggedIn 
          ? (cartEmail || shippingEmail || prev?.email || "")
          : (shippingEmail || prev?.email || "");
        
        return {
          ...prev,
          // Only update fields if cart has non-empty values, otherwise preserve existing
          pincode: hasValue(shipping?.postalCode) ? shipping.postalCode : (prev?.pincode || ""),
          address: hasValue(shipping?.line1) ? shipping.line1 : (prev?.address || ""),
          line2: hasValue(shipping?.line2) ? shipping.line2 : (prev?.line2 || ""),
          cityValue: hasValue(shipping?.city) ? shipping.city : (prev?.cityValue || ""),
          stateValue:
            (stateDropdown || locale === 'zh-tw')
              ? (locale === 'zh-tw' ? "TW" : (hasValue(shipping?.countrySubdivision) ? shipping.countrySubdivision : (prev?.stateValue || '')))
              : (prev?.stateValue || ''),
          county: countyField ? (hasValue(shipping?.county) ? shipping.county : (prev?.county || "")) : (prev?.county || ''),
          countryValue: hasValue(shipping?.country) ? shipping.country : (prev?.countryValue || ""),
          countryName: hasValue(shipping?.countryName) ? shipping.countryName : (prev?.countryName || ""),
          firstName: hasValue(shipping?.firstName) ? shipping.firstName : (prev?.firstName || ""),
          lastName: hasValue(shipping?.lastName) ? shipping.lastName : (prev?.lastName || ""),
          attn: hasValue(shipping?.attn) ? shipping.attn : (prev?.attn && prev.attn.trim() !== "" ? prev?.attn : ""),
          contact: hasValue(shipping?.phoneNumber) ? shipping.phoneNumber : (prev?.contact || ""),
          // Email fields - for logged-in users, prioritize cartData.emailAddress
          email: emailToUse,
          vemail: emailToUse,
        };
      });

      setSelectedShippingAddress({
        ...(shipping.id ? { id: Number(shipping.id) } : {}),
        city: shipping.city || "",
        countryName: shipping.countryName,
        country: shipping.country,
        firstName: shipping.firstName || "",
        isDefault: true,
        lastName: shipping.lastName || "",
        attn: shipping.attn || "",
        line1: shipping.line1 || "",
        line2: shipping.line2 || "",
        nickName: "",
        phoneNumber: shipping.phoneNumber || "",
        postalCode: shipping.postalCode || "",
        countrySubdivision:
          (stateDropdown || locale === 'zh-tw')
            ? (locale === 'zh-tw' ? "TW" : shipping.countrySubdivision)
            : '',
        county: countyField ? shipping.county || "" : '',
      });

      setFormTaxData({
        companyName: "",
        vatNo: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Helper function to match cart address with addresses array
   * First tries to match by id, then by address fields
   */
  const matchAddressInArray = useCallback((cartAddress: Address, addressesArray: intrfce.iSavedAddress[]): intrfce.iSavedAddress | null => {
    if (!cartAddress || !addressesArray || addressesArray.length === 0) {
      return null;
    }

    // Match by id only: saved addresses (id > 0) match only by id, new address (id === -1) also matches by id
    if (cartAddress.id !== undefined && cartAddress.id !== null && typeof cartAddress.id === 'number') {
      const matchById = addressesArray.find(addr => Number(addr.id) === cartAddress.id);
      if (matchById) return matchById;
    }

    // If no id match and cart address has no id, try to match with id === -1 entry by fields
    // This handles the case where a new address was submitted and comes back from verify page without id
    // Note: Saved addresses (id > 0) are matched ONLY by id, never by fields
    if (cartAddress.id === undefined || cartAddress.id === null) {
      const newAddressEntry = addressesArray.find(addr => addr.id === -1);
      if (newAddressEntry) {
        // Normalize addresses for comparison
        const normalizeAddr = (addr: any) => ({
          line1: (addr.line1 || '').trim().toLowerCase(),
          city: (addr.city || '').trim().toLowerCase(),
          postalCode: (addr.postalCode || '').trim().toLowerCase(),
          firstName: (addr.firstName || '').trim().toLowerCase(),
          lastName: (addr.lastName || '').trim().toLowerCase(),
        });

        const normalizedCart = normalizeAddr(cartAddress);
        const normalizedNew = normalizeAddr(newAddressEntry);

        // If fields match, return the id === -1 entry (will be updated with cart data)
        if (normalizedCart.line1 === normalizedNew.line1 &&
          normalizedCart.city === normalizedNew.city &&
          normalizedCart.postalCode === normalizedNew.postalCode &&
          normalizedCart.firstName === normalizedNew.firstName &&
          normalizedCart.lastName === normalizedNew.lastName) {
          return newAddressEntry;
        }
      }
    }

    // No match found
    return null;
  }, []);

  /**
   * Handles successful cart operations
   */
  const handleSuccess = useCallback((result: any, pid?: number) => {
    const pageLocale = window.location.pathname.split("/")[1];
    let cartState = result?.cartItems?.cartItem;
    let billingFlag = sessionStorage.getItem("isBilling");
    
    // Check if we're just navigating between steps (not submitting data)
    // If URL has step=1 param, we're navigating back to address page - skip address updates
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get("step");
    const isNavigatingBetweenSteps = stepParam === "1" || stepParam === "2";

    setBrand(result?.brand?.toUpperCase());
    
    // Handle tax exemption
    if (result.taxExemption && result.taxExemption.companyName && result.taxExemption.vatNo) {
      setFormTaxData({
        companyName: result.taxExemption.companyName,
        vatNo: result.taxExemption.vatNo,
      });
      setIsBusinessPurchase(true);
    }
    
    // Handle billing flag (whether billing is different from shipping)
    if (billingFlag && billingFlag.length) {
      setIsBilling(JSON.parse(billingFlag));
    }
    
    if (cartState && cartState.length > 0) {
      // Validate locale
      if (result?.locale) {
        const localeParts = result.locale.split('_');
        if (localeParts.length === 2) {
          const formattedResultLocale = `${localeParts[0]}-${localeParts[1].toLowerCase()}`;
          if (pageLocale !== formattedResultLocale) {
            hideBodyLoader();
            emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
            return;
          }
        }
      }

      // Build cart object for analytics
      const items = cartState.map((cartItem: any) => ({
        quantity: cartItem.quantity,
        price: {
          basePrice: parseFloat(
            cartItem.pricing.formattedListPrice.replace(currencySign, "")
          ),
          discount: 0,
        },
        productInfo: {
          sku: `sku-${cartItem.product.id}`,
          prodID: cartItem.product.id.toString(),
          productName: cartItem.product.name,
        },
      }));

      const formattedLocale = pageLocale
        .replace("-", "_")
        .toLowerCase()
        .replace(/^[a-z]{2}/, (match) => match.toUpperCase());
        
      const cart = {
        customerID: "",
        orderID: result ? result["cartId"] : "",
        currencyCode: result ? result["pricing"]["orderTotal"]["currency"] : "",
        loginState: nvCookieHelpers.getCookie("isUserLoggedIn") ? "loggedIn" : "guest",
        storeLocale: formattedLocale,
        promoCode: "no",
        saveCardOption: false,
        shippingMethod: result ? result["shippingMethod"]["description"] : "",
        items: items,
      };
      
      if (typeof window !== "undefined") {
        (window as any).CartOrder = cart;
      }
    } else {
      emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
    }
    
    console.log((window as any).CartOrder);
    
    if (result.totalItemsInCart) {
      let billing = result?.billingAddress;
      let shipping = result?.shippingAddress;
      setCartData(result);
      setItemsInCart(result.totalItemsInCart);

      // Navigate based on login status
      if (result.totalItemsInCart > 0) {
        if (nvCookieHelpers.getCookie("isUserLoggedIn")) {
          navigate(`/${pageLocale}/cart/user/loggedIn`);
        } else {
          navigate(`/${pageLocale}/cart/user/guest`);
        }
      } else {
        emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
      }

      // Set shipping options
      if (cartState && cartState.length > 0) {
        setShippingList(result.shippingOptions.shippingOption);
        setShippingOptions({
          shippingOption: result.shippingMethod.code
        });
      } else {
        if (nvCookieHelpers.getCookie("isUserLoggedIn")) {
          orderAccount();
        } else {
          emptyCart("", pid);
        }
      }
      
      // Helper function to check if a value is a non-empty string
      const hasValue = (val: any) => val && typeof val === 'string' && val.trim().length > 0;

      // Check if cart API has complete address data (before declaring billing/shipping variables)
      const billingCheck = result?.billingAddress;
      const shippingCheck = result?.shippingAddress;
      const cartHasBillingData = billingCheck && hasValue(billingCheck.line1) && hasValue(billingCheck.city) && hasValue(billingCheck.postalCode);
      const cartHasShippingData = shippingCheck && hasValue(shippingCheck.line1) && hasValue(shippingCheck.city) && hasValue(shippingCheck.postalCode);
      const cartHasAddressData = cartHasBillingData || cartHasShippingData;

      // Only skip address updates if:
      // 1. We're navigating between steps (step param exists)
      // 2. AND cart API doesn't have complete address data
      // If cart has complete data, always update (e.g., coming from verify page)
      const shouldSkipAddressUpdates = isNavigatingBetweenSteps && !cartHasAddressData;

      // Handle logged-in user addresses
      if (!shouldSkipAddressUpdates) {
        if (nvCookieHelpers.getCookie("isUserLoggedIn")) {
          const addresses = result["addresses"] || [];
          console.log('Raw addresses from backend:', addresses);

          // Ensure at least one address has isDefault flag
          if (addresses?.length && !addresses.some((addr: any) => addr.isDefault)) {
            addresses[0].isDefault = true;
          }

          // Initialize addresses array in saved addresses if not already set
          // Also clean up any duplicate "New Address" entries when initializing
          if (addresses?.length) {
            if (!savedBillingAddress?.length) {
              setSavedBillingAddress(addresses);
            } else {
              // Merge backend addresses with existing, removing duplicates
              setSavedBillingAddress((prev) => {
                if (!prev) return addresses;

                // Remove any "New Address" entries (id === -1) that match backend addresses
                const normalizeAddr = (addr: any) => ({
                  line1: (addr.line1 || '').trim().toLowerCase(),
                  city: (addr.city || '').trim().toLowerCase(),
                  postalCode: (addr.postalCode || '').trim().toLowerCase(),
                  firstName: (addr.firstName || '').trim().toLowerCase(),
                  lastName: (addr.lastName || '').trim().toLowerCase(),
                });

                // Filter out addresses that match backend addresses
                // Keep only saved addresses (id > 0) and one "New Address" (id === -1) if it doesn't match backend
                const filteredPrev = prev.filter(prevAddr => {
                  // Remove recommended addresses (id === 0) - not part of simplified approach
                  if (prevAddr.id === 0) {
                    return false;
                  }

                  // For "New Address" (id === -1), keep it only if it doesn't match any backend address
                  if (prevAddr.id === -1) {
                    const normalizedPrev = normalizeAddr(prevAddr);
                    // Check if this "New Address" matches any backend address
                    const matchesBackend = addresses.some((backendAddr: any) => {
                      const normalizedBackend = normalizeAddr(backendAddr);
                      return normalizedPrev.line1 === normalizedBackend.line1 &&
                        normalizedPrev.city === normalizedBackend.city &&
                        normalizedPrev.postalCode === normalizedBackend.postalCode &&
                        normalizedPrev.firstName === normalizedBackend.firstName &&
                        normalizedPrev.lastName === normalizedBackend.lastName;
                    });
                    // Keep if it doesn't match a backend address
                    return !matchesBackend;
                  }

                  // Keep saved addresses (id > 0)
                  return true;
                });

                // Merge backend addresses with filtered existing addresses
                // Backend addresses (saved addresses with id > 0) come first
                // Then add the "New Address" entry (id === -1) if it exists
                const merged = [...addresses];
                const newAddressEntry = filteredPrev.find(addr => addr.id === -1);
                if (newAddressEntry) {
                  merged.push(newAddressEntry);
                }

                return merged;
              });
            }

            if (!savedShippingAddress?.length) {
              setSavedShippingAddress(addresses);
            } else {
              // Merge backend addresses with existing, removing duplicates
              setSavedShippingAddress((prev) => {
                if (!prev) return addresses;

                // Remove any "New Address" entries (id === -1) that match backend addresses
                const normalizeAddr = (addr: any) => ({
                  line1: (addr.line1 || '').trim().toLowerCase(),
                  city: (addr.city || '').trim().toLowerCase(),
                  postalCode: (addr.postalCode || '').trim().toLowerCase(),
                  firstName: (addr.firstName || '').trim().toLowerCase(),
                  lastName: (addr.lastName || '').trim().toLowerCase(),
                });

                // Filter out addresses that match backend addresses
                // Keep only saved addresses (id > 0) and one "New Address" (id === -1) if it doesn't match backend
                const filteredPrev = prev.filter(prevAddr => {
                  // Remove recommended addresses (id === 0) - not part of simplified approach
                  if (prevAddr.id === 0) {
                    return false;
                  }

                  // For "New Address" (id === -1), keep it only if it doesn't match any backend address
                  if (prevAddr.id === -1) {
                    const normalizedPrev = normalizeAddr(prevAddr);
                    // Check if this "New Address" matches any backend address
                    const matchesBackend = addresses.some((backendAddr: any) => {
                      const normalizedBackend = normalizeAddr(backendAddr);
                      return normalizedPrev.line1 === normalizedBackend.line1 &&
                        normalizedPrev.city === normalizedBackend.city &&
                        normalizedPrev.postalCode === normalizedBackend.postalCode &&
                        normalizedPrev.firstName === normalizedBackend.firstName &&
                        normalizedPrev.lastName === normalizedBackend.lastName;
                    });
                    // Keep if it doesn't match a backend address
                    return !matchesBackend;
                  }

                  // Keep saved addresses (id > 0)
                  return true;
                });

                // Merge backend addresses with filtered existing addresses
                // Backend addresses (saved addresses with id > 0) come first
                // Then add the "New Address" entry (id === -1) if it exists
                const merged = [...addresses];
                const newAddressEntry = filteredPrev.find(addr => addr.id === -1);
                if (newAddressEntry) {
                  merged.push(newAddressEntry);
                }

                return merged;
              });
            }
          }
          // PRIORITY LOGIC: Check cart addresses first, then fallback to addresses array default
          const billingData = Array.isArray(billing) ? billing[0] : billing;
          const shippingData = Array.isArray(shipping) ? shipping[0] : shipping;
          
          // Maintain isBilling state - check if billing and shipping are different
          if (cartHasBillingData && cartHasShippingData && billingData && shippingData) {
            const { attn: _, invalidAddress: __, ...shippingWithoutAttn } = shippingData;
            const billingDifferent = JSON.stringify(billingData) !== JSON.stringify(shippingWithoutAttn);
            if (billingDifferent) {
              setIsBilling(true);
            }
          } else if (billingFlag && billingFlag.length) {
            // Preserve existing isBilling state if available
            setIsBilling(JSON.parse(billingFlag));
          }

          // Handle billing address with priority logic
          if (cartHasBillingData && billingData) {
            // PRIORITY 1: Cart has billing address - match with addresses array first, then saved addresses
            // Use saved addresses if addresses array is empty (no saved addresses from backend)
            const addressesToMatch = addresses?.length > 0 ? addresses : (savedBillingAddress || []);
            const matchedBilling = matchAddressInArray(billingData, addressesToMatch);

            if (matchedBilling) {
              // Match found: use matched address from array; apply US char limits for saved addresses
              const formAddress = savedAddressToFormAddress(matchedBilling, { locale, stateDropdown, countyField });
              const cartEmail = (result?.emailAddress && result.emailAddress.trim()) ? result.emailAddress : null;
              setFormBillingAddress((prev) => applyUSAddressFieldLimits({
                ...formAddress,
                firstName: matchedBilling.firstName || prev?.firstName || "",
                lastName: matchedBilling.lastName || prev?.lastName || "",
                contact: matchedBilling.phoneNumber || prev?.contact || "",
                email: cartEmail || prev?.email || "",
                vemail: cartEmail || prev?.vemail || "",
              }));
              setSelectedBillingAddress(matchedBilling);
              // Saved addresses (id > 0) are readonly, new address (id === -1) is editable
              setIsBillingReadonly(Number(matchedBilling.id) > 0 && userType === "loggedIn");
            } else {
              // No match: This is a new address - update or create the id === -1 entry
              const newAddress = createAddressFromCartData(billingData, { locale, stateDropdown, countyField }, "New Address");
              newAddress.id = -1;

              // Update existing id === -1 entry or add if it doesn't exist
              setSavedBillingAddress((prev) => {
                if (!prev || prev.length === 0) {
                  return [newAddress];
                }

                // Find existing id === -1 entry
                const existingNewAddressIndex = prev.findIndex(addr => addr.id === -1);

                if (existingNewAddressIndex !== -1) {
                  // Update existing id === -1 entry
                  const updated = [...prev];
                  updated[existingNewAddressIndex] = newAddress;
                  return updated;
                } else {
                  // Add new id === -1 entry (only one should exist)
                  return [newAddress, ...prev];
                }
              });

              // Populate form from cart address; apply US char limits
              const cartEmailNew = (result?.emailAddress && result.emailAddress.trim()) ? result.emailAddress : null;
              setFormBillingAddress((prev) => applyUSAddressFieldLimits({
                ...prev,
                pincode: billingData.postalCode || prev?.pincode || "",
                address: billingData.line1 || prev?.address || "",
                line2: billingData.line2 || prev?.line2 || "",
                cityValue: billingData.city || prev?.cityValue || "",
                stateValue: (stateDropdown || locale === 'zh-tw')
                  ? (locale === 'zh-tw' ? "TW" : (billingData.countrySubdivision || prev?.stateValue || ''))
                  : (prev?.stateValue || ''),
                county: countyField ? (billingData.county || prev?.county || "") : (prev?.county || ''),
                countryName: billingData.countryName || prev?.countryName || "",
                countryValue: billingData.country || prev?.countryValue || "",
                firstName: billingData.firstName || prev?.firstName || "",
                lastName: billingData.lastName || prev?.lastName || "",
                contact: billingData.phoneNumber || prev?.contact || "",
                email: cartEmailNew || prev?.email || "",
                vemail: cartEmailNew || prev?.vemail || "",
                id: -1,
              }));
              setSelectedBillingAddress(newAddress);
              setIsBillingReadonly(false);
            }
          } else if (addresses?.length > 0) {
            // PRIORITY 2: Fallback to default from addresses array; apply US char limits
            const defaultBilling = addresses.find((addr: any) => addr.isDefault) || addresses[0];
            const formAddress = savedAddressToFormAddress(defaultBilling, { locale, stateDropdown, countyField });
            const cartEmailDefault = (result?.emailAddress && result.emailAddress.trim()) ? result.emailAddress : null;
            setFormBillingAddress((prev) => applyUSAddressFieldLimits({
              ...formAddress,
              firstName: defaultBilling.firstName || prev?.firstName || "",
              lastName: defaultBilling.lastName || prev?.lastName || "",
              contact: defaultBilling.phoneNumber || prev?.contact || "",
              email: cartEmailDefault || prev?.email || "",
              vemail: cartEmailDefault || prev?.vemail || "",
            }));
            setSelectedBillingAddress(defaultBilling);
            setIsBillingReadonly(true);
          } else {
            // PRIORITY 3: No addresses - fields empty and editable
            setIsBillingReadonly(false);
          }

          // Handle shipping address with priority logic
          if (cartHasShippingData && shippingData) {
            // PRIORITY 1: Cart has shipping address - match with addresses array first, then saved addresses
            // Use saved addresses if addresses array is empty (no saved addresses from backend)
            const addressesToMatch = addresses?.length > 0 ? addresses : (savedShippingAddress || []);
            const matchedShipping = matchAddressInArray(shippingData, addressesToMatch);

            if (matchedShipping) {
              // Match found: use matched address from array; apply US char limits for saved addresses
              const formAddress = savedAddressToFormAddress(matchedShipping, { locale, stateDropdown, countyField });
              const cartEmail = (result?.emailAddress && result.emailAddress.trim()) ? result.emailAddress : null;
              setFormShippingAddress((prev) => applyUSAddressFieldLimits({
                ...formAddress,
                firstName: matchedShipping.firstName || prev?.firstName || "",
                lastName: matchedShipping.lastName || prev?.lastName || "",
                attn: matchedShipping.attn || prev?.attn || "",
                contact: matchedShipping.phoneNumber || prev?.contact || "",
                email: cartEmail || prev?.email || "",
                vemail: cartEmail || prev?.vemail || "",
              }));
              setSelectedShippingAddress(matchedShipping);
              // Saved addresses (id > 0) are readonly, new address (id === -1) is editable
              setIsShippingReadonly(Number(matchedShipping.id) > 0 && userType === "loggedIn");
            } else {
              // No match: This is a new address - update or create the id === -1 entry
              const newAddress = createAddressFromCartData(shippingData, { locale, stateDropdown, countyField }, "New Address");
              newAddress.id = -1;
              newAddress.attn = shippingData.attn || "";

              // Update existing id === -1 entry or add if it doesn't exist
              setSavedShippingAddress((prev) => {
                if (!prev || prev.length === 0) {
                  return [newAddress];
                }

                // Find existing id === -1 entry
                const existingNewAddressIndex = prev.findIndex(addr => addr.id === -1);

                if (existingNewAddressIndex !== -1) {
                  // Update existing id === -1 entry
                  const updated = [...prev];
                  updated[existingNewAddressIndex] = newAddress;
                  return updated;
                } else {
                  // Add new id === -1 entry (only one should exist)
                  return [newAddress, ...prev];
                }
              });

              // Populate form from cart address; apply US char limits
              const cartEmailNew = (result?.emailAddress && result.emailAddress.trim()) ? result.emailAddress : null;
              setFormShippingAddress((prev) => applyUSAddressFieldLimits({
                ...prev,
                pincode: shippingData.postalCode || prev?.pincode || "",
                address: shippingData.line1 || prev?.address || "",
                line2: shippingData.line2 || prev?.line2 || "",
                cityValue: shippingData.city || prev?.cityValue || "",
                stateValue: (stateDropdown || locale === 'zh-tw')
                  ? (locale === 'zh-tw' ? "TW" : (shippingData.countrySubdivision || prev?.stateValue || ''))
                  : (prev?.stateValue || ''),
                county: countyField ? (shippingData.county || prev?.county || "") : (prev?.county || ''),
                countryName: shippingData.countryName || prev?.countryName || "",
                countryValue: shippingData.country || prev?.countryValue || "",
                firstName: shippingData.firstName || prev?.firstName || "",
                lastName: shippingData.lastName || prev?.lastName || "",
                attn: shippingData.attn || prev?.attn || "",
                contact: shippingData.phoneNumber || prev?.contact || "",
                email: cartEmailNew || prev?.email || "",
                vemail: cartEmailNew || prev?.vemail || "",
                id: -1,
              }));
              setSelectedShippingAddress(newAddress);
              setIsShippingReadonly(false);
            }
          } else if (addresses?.length > 0) {
            // PRIORITY 2: Fallback to default from addresses array; apply US char limits
            const defaultShipping = addresses.find((addr: any) => addr.isDefault) || addresses[0];
            const formAddress = savedAddressToFormAddress(defaultShipping, { locale, stateDropdown, countyField });
            const cartEmailDefault = (result?.emailAddress && result.emailAddress.trim()) ? result.emailAddress : null;
            setFormShippingAddress((prev) => applyUSAddressFieldLimits({
              ...formAddress,
              firstName: defaultShipping.firstName || prev?.firstName || "",
              lastName: defaultShipping.lastName || prev?.lastName || "",
              attn: defaultShipping.attn || prev?.attn || "",
              contact: defaultShipping.phoneNumber || prev?.contact || "",
              email: cartEmailDefault || prev?.email || "",
              vemail: cartEmailDefault || prev?.vemail || "",
            }));
            setSelectedShippingAddress(defaultShipping);
            setIsShippingReadonly(true);
          } else {
            // PRIORITY 3: No addresses - fields empty and editable
            setIsShippingReadonly(false);
          }
        } // End loggedIn block

        // Handle billing address from cart (for guest users only - logged-in users handled above)
        // Only update if cart API has meaningful address data to avoid overwriting valid form data
        if (!nvCookieHelpers.getCookie("isUserLoggedIn")) {
          let cartHasAddressData = false;
          if (result?.billingAddress) {
            const isBillingDifferent = billingFlag && JSON.parse(billingFlag);

          // Check if cart API has meaningful address data (at least line1, city, and postalCode)
          cartHasAddressData = hasValue(billing.line1) && hasValue(billing.city) && hasValue(billing.postalCode);

        // Only update address fields if cart API has complete data, otherwise preserve existing form data
        if (cartHasAddressData) {
          // For logged-in users, email comes from result?.emailAddress (root level)
          const isLoggedIn = nvCookieHelpers.getCookie("isUserLoggedIn");
          const billingEmail = hasValue(billing.emailAddress) ? billing.emailAddress : null;
          const cartEmail = hasValue(result?.emailAddress) ? result.emailAddress : null;
          const emailToUse = isLoggedIn 
            ? (cartEmail || billingEmail || null)
            : (billingEmail || cartEmail || null);
          
          setFormBillingAddress((prev) => {
            // Restore address fields from cart API (cart has complete data); apply US char limits
            return applyUSAddressFieldLimits({
              ...prev,
              ...(billing.id ? { id: Number(billing.id) } : {}),
              // Address fields - use cart API values
              pincode: hasValue(billing.postalCode) ? billing.postalCode : prev?.pincode,
              address: hasValue(billing.line1) ? billing.line1 : prev?.address,
              line2: hasValue(billing.line2) ? billing.line2 : prev?.line2,
              cityValue: hasValue(billing.city) ? billing.city : prev?.cityValue,
              stateValue:
                (stateDropdown || locale === 'zh-tw')
                  ? (locale === 'zh-tw' ? "TW" : (hasValue(billing.countrySubdivision) ? billing.countrySubdivision : prev?.stateValue))
                  : prev?.stateValue,
              county: countyField 
                ? (hasValue(billing.county) ? billing.county : prev?.county)
                : prev?.county,
              countryName: hasValue(billing.countryName) ? billing.countryName : prev?.countryName,
              countryValue: hasValue(billing.country) ? billing.country : prev?.countryValue,
              // Contact fields - use cart API if available, otherwise preserve (same pattern for all)
              firstName: hasValue(billing.firstName) ? billing.firstName : prev?.firstName,
              lastName: hasValue(billing.lastName) ? billing.lastName : prev?.lastName,
              contact: hasValue(billing.phoneNumber) ? billing.phoneNumber : prev?.contact,
              // Email fields - for logged-in users, prioritize result?.emailAddress (root level)
              email: emailToUse || prev?.email || "",
              vemail: emailToUse || prev?.vemail || ""
            });
          });

          setSelectedBillingAddress({
            ...(billing.id ? { id: Number(billing.id) } : {}),
            city: billing.city || "",
            countryName: billing.countryName || "",
            country: billing.country || "",
            firstName: billing.firstName || "",
            lastName: billing.lastName || "",
            line1: billing.line1 || "",
            line2: billing.line2 || '',
            nickName: "",
            phoneNumber: billing.phoneNumber || "",
            postalCode: billing.postalCode || "",
            countrySubdivision: (stateDropdown || locale === 'zh-tw')
              ? (locale === 'zh-tw' ? "TW" : (billing.countrySubdivision || ''))
              : '',
            county: countyField ? billing.county || '' : '',
          });
        }
        
        if (Number(billing.id) > -1) {
          if (userType === "loggedIn" && savedBillingAddress.length) {
            setIsBillingReadonly(true);
          } else {
            setIsBillingReadonly(false);
          }
        }

        // Handle shipping address
        if (result?.shippingAddress) {
          const isBillingDifferent = billingFlag && JSON.parse(billingFlag);
          
          // Check if cart API has meaningful shipping address data
          const cartHasShippingData = hasValue(shipping.line1) && hasValue(shipping.city) && hasValue(shipping.postalCode);

          // Only update shipping address if cart API has complete data
          if (cartHasShippingData) {
            // For logged-in users, email comes from result?.emailAddress (root level)
            const isLoggedIn = nvCookieHelpers.getCookie("isUserLoggedIn");
            const shippingEmail = hasValue(shipping.emailAddress) ? shipping.emailAddress : null;
            const cartEmail = hasValue(result?.emailAddress) ? result.emailAddress : null;
            const emailToUse = isLoggedIn 
              ? (cartEmail || shippingEmail || null)
              : (shippingEmail || cartEmail || null);
            
            setFormShippingAddress((prev) => {
              // Restore address fields from cart API (cart has complete data); apply US char limits
              return applyUSAddressFieldLimits({
                ...prev,
                // Address fields - use cart API values
                pincode: hasValue(shipping.postalCode) ? shipping.postalCode : prev?.pincode,
                address: hasValue(shipping.line1) ? shipping.line1 : prev?.address,
                line2: hasValue(shipping.line2) ? shipping.line2 : prev?.line2,
                cityValue: hasValue(shipping.city) ? shipping.city : prev?.cityValue,
                stateValue:
                  (stateDropdown || locale === 'zh-tw')
                    ? (locale === 'zh-tw' ? "TW" : (hasValue(shipping.countrySubdivision) ? shipping.countrySubdivision : prev?.stateValue))
                    : prev?.stateValue,
                county:
                  locale !== "en-us"
                    ? (hasValue(shipping.county) ? shipping.county : prev?.county)
                    : prev?.county,
                countryValue: hasValue(shipping.country) ? shipping.country : prev?.countryValue,
                countryName: hasValue(shipping.countryName) ? shipping.countryName : prev?.countryName,
                // Contact fields - use cart API if available, otherwise preserve (same pattern for all)
                firstName: hasValue(shipping?.firstName) ? shipping.firstName : prev?.firstName,
                lastName: hasValue(shipping?.lastName) ? shipping.lastName : prev?.lastName,
                attn: hasValue(shipping?.attn) ? shipping.attn : (prev?.attn && prev.attn.trim() !== "" ? prev?.attn : ""),
                contact: hasValue(shipping?.phoneNumber) ? shipping.phoneNumber : prev?.contact,
                // Email fields - for logged-in users, prioritize result?.emailAddress (root level)
                email: emailToUse || prev?.email || "",
                vemail: emailToUse || prev?.vemail || "",
              });
            });

            setSelectedShippingAddress(
              isBillingDifferent ? shipping : billing
            );

            if (Number(shipping.id) > -1) {
              if (userType === "loggedIn" && savedShippingAddress.length) {
                setIsShippingReadonly(true);
              } else {
                setIsShippingReadonly(false);
              }
            }
          }
        } else if (!isBillingDifferent && result?.billingAddress && cartHasAddressData) {
          // When billing and shipping are the same and billing has complete data,
          // update shipping to match billing
          
          // For logged-in users, email comes from result?.emailAddress (root level)
          const isLoggedIn = nvCookieHelpers.getCookie("isUserLoggedIn");
          const billingEmail = hasValue(billing.emailAddress) ? billing.emailAddress : null;
          const cartEmail = hasValue(result?.emailAddress) ? result.emailAddress : null;
          const emailToUse = isLoggedIn 
            ? (cartEmail || billingEmail || null)
            : (billingEmail || cartEmail || null);
          
          setFormShippingAddress((prev) => {
            // Restore shipping address from billing address (billing has complete data)
            return {
              ...prev,
              // Address fields - use billing address values
              pincode: hasValue(billing.postalCode) ? billing.postalCode : prev?.pincode,
              address: hasValue(billing.line1) ? billing.line1 : prev?.address,
              line2: hasValue(billing.line2) ? billing.line2 : prev?.line2,
              cityValue: hasValue(billing.city) ? billing.city : prev?.cityValue,
              stateValue:
                (stateDropdown || locale === 'zh-tw')
                  ? (locale === 'zh-tw' ? "TW" : (hasValue(billing.countrySubdivision) ? billing.countrySubdivision : prev?.stateValue))
                  : prev?.stateValue,
              county: countyField 
                ? (hasValue(billing.county) ? billing.county : prev?.county)
                : prev?.county,
              countryValue: hasValue(billing.country) ? billing.country : prev?.countryValue,
              countryName: hasValue(billing.countryName) ? billing.countryName : prev?.countryName,
              // Contact fields - use billing if available, otherwise preserve
              firstName: hasValue(billing.firstName) ? billing.firstName : prev?.firstName,
              lastName: hasValue(billing.lastName) ? billing.lastName : prev?.lastName,
              attn: (prev?.attn && prev.attn.trim() !== "" ? prev?.attn : ""), // Preserve attn (shipping-only field)
              contact: hasValue(billing.phoneNumber) ? billing.phoneNumber : prev?.contact,
              // Email fields - for logged-in users, prioritize result?.emailAddress (root level)
              email: emailToUse || prev?.email || "",
              vemail: emailToUse || prev?.vemail || "",
            };
          });
          
          setSelectedShippingAddress(billing);
        }
          }
        } // End guest user address handling
      }
      hideBodyLoader();
    } else {
      if (nvCookieHelpers.getCookie("isUserLoggedIn")) {
        orderAccount();
      } else {
        emptyCart("", pid);
      }
    }
  }, [
    currencySign,
    locale,
    countyField,
    userType,
    savedBillingAddress,
    savedShippingAddress, stateDropdown,
    t,
    setBrand,
    setFormTaxData,
    setIsBusinessPurchase,
    setIsBilling,
    setCartData,
    setShippingList,
    setShippingOptions,
    setSavedBillingAddress,
    setSavedShippingAddress,
    setFormBillingAddress,
    setFormShippingAddress,
    setSelectedBillingAddress,
    setIsBillingReadonly,
    setSelectedShippingAddress,
    setIsShippingReadonly,
    emptyCart,
    orderAccount,
    navigate,
    setItemsInCart,
    hideBodyLoader,
    matchAddressInArray
  ]);

  /**
   * Handles cart quantity changes
   * Note: This is a simplified version. The actual implementation in User.tsx is more complex.
   * This function is not exported as User.tsx has its own implementation.
   */
  const handleCartQuantityChange = useCallback(async (
    productId: string,
    quantity: number,
    onSuccess: () => void
  ) => {
    await updateCart(
      handleSuccess,
      (error: any) => {
        console.error("Cart update error:", error);
      },
      productId,
      quantity
    );
    onSuccess();
  }, [updateCart, handleSuccess]);

  /**
   * Handles coupon/promo code changes
   * Note: This is a simplified version. The actual implementation in User.tsx is more complex.
   * This function is not exported as User.tsx has its own implementation.
   */
  const handleCouponChange = useCallback((fieldName: string, promo: string | null) => {
    // Clear any previous promo errors
    setPromoErr(false);
    setValidationErrors((prevErrors: any) => ({
      ...prevErrors,
      promoCode: "",
    }));
  }, [setPromoErr, setValidationErrors]);

  /**
   * Handles coupon deletion
   * Note: This is a simplified version. The actual implementation in User.tsx is more complex.
   * This function is not exported as User.tsx has its own implementation.
   */
  const handleCouponDelete = useCallback((d: boolean) => {
    // Coupon deletion logic
    setPromoErr(false);
  }, [setPromoErr]);

  /**
   * Handles coupon submission
   */
  const handleCouponSubmit = useCallback((c: string) => {
    // Coupon submission logic
    console.log("Coupon submitted:", c);
  }, []);

  /**
   * Handles cart errors
   */
  const handleCartError = useCallback((error: any) => {
    hideBodyLoader();
    console.error("Cart error:", error);
    
    // Navigate back to Step 1 for any error
    // Note: setCurrentStep is handled by User.tsx
    
    // Handle error object
    if (error && error.code === "postal_code_invalid") {
      console.error("Postal code invalid:", error.message);
      setValidationErrors((prevErrors: any) => ({
        ...prevErrors,
        addressErr: error?.description,
      }));
    } else if (error && error.code === 'restricted-ship-to-country') {
      console.log('🚨 Restricted ship-to-country error:', error);
      setValidationErrors((prevErrors: any) => ({
        ...prevErrors,
        shippingAddressErr: t('ADDRESS_NOT_SHIPPABLE'),
      }));
    } else if (error?.message) {
      setValidationErrors((prevErrors: any) => ({
        ...prevErrors,
        cartError: error.message,
      }));
    }
  }, [hideBodyLoader, setValidationErrors, t]);

  /**
   * Handles cart change navigation
   */
  const handleCartChange = useCallback(async (
    params: any,
    updateCartFunction: (params: any, onSuccess: () => void, onError: (error: any) => void) => void
  ) => {
    showBodyLoader();
    await updateCartFunction(
      params,
      () => {
        hideBodyLoader();
        if (userType) {
          navigate(`/${locale}/cart/verify/${userType}`);
        }
      },
      (error: any) => {
        hideBodyLoader();
        console.error("Cart change error:", error);
      }
    );
  }, [showBodyLoader, hideBodyLoader, navigate, locale, userType]);

  /**
   * Handles successful cart operations (wrapper for handleSuccess)
   */
  const handleCartSuccess = useCallback((res: any) => {
    if (res?.paymentSession?.id) {
      sessionStorage.setItem("paymentSession", res?.paymentSession?.id);
    }
    // Note: setTemporaryAddress is handled by User.tsx
    if (userType) {
      showBodyLoader();
      reviewOrder(userType);      
    }
  }, [userType, showBodyLoader, reviewOrder]);

  return {
    // Cart initialization (handled by useEffect)
    
    // Cart success handler
    handleSuccess,
    handleCartSuccess,
    
    // Cart operations
    handleCartQuantityChange,
    handleCouponChange,
    handleCouponDelete,
    handleCouponSubmit,
    handleCartError,
    handleCartChange
  };
};
